import { useState, useEffect } from 'react'
import {
  CreditCardIcon,
  BanknotesIcon,
  ChartBarIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid'
import ProfessionalOnboarding from './ProfessionalOnboarding'
import { getProfessionalEarnings, PaymentTransfer } from '../utils/stripeConnect'

interface BillingPaymentsProps {
  userType: 'client' | 'professional'
  userId: string
  userEmail: string
}

interface EarningsSummary {
  totalEarnings: number
  pendingPayouts: number
  completedPayouts: number
  platformFees: number
  transactionCount: number
  transactions: PaymentTransfer[]
}

interface ClientPayment {
  id: string
  professionalName: string
  sessionType: string
  amount: number
  currency: string
  status: 'completed' | 'pending' | 'refunded'
  date: string
  receiptUrl?: string
}

export default function BillingPayments({ userType, userId, userEmail }: BillingPaymentsProps) {
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [earnings, setEarnings] = useState<EarningsSummary | null>(null)
  const [clientPayments, setClientPayments] = useState<ClientPayment[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions' | 'settings'>('overview')

  useEffect(() => {
    loadData()
  }, [userType, userId])

  const loadData = async () => {
    try {
      setLoading(true)

      if (userType === 'professional') {
        const earningsData = await getProfessionalEarnings(userId)
        setEarnings(earningsData)
      } else {
        // Load client payment history
        const mockClientPayments: ClientPayment[] = [
          {
            id: 'pay_1',
            professionalName: 'Dr. Sarah Johnson',
            sessionType: 'Career Coaching',
            amount: 10000, // $100.00 in cents
            currency: 'usd',
            status: 'completed',
            date: new Date(Date.now() - 86400000).toISOString(),
            receiptUrl: '#'
          },
          {
            id: 'pay_2',
            professionalName: 'Michael Foster',
            sessionType: 'Interview Prep',
            amount: 15000, // $150.00 in cents
            currency: 'usd',
            status: 'completed',
            date: new Date(Date.now() - 172800000).toISOString(),
            receiptUrl: '#'
          }
        ]
        setClientPayments(mockClientPayments)
      }
    } catch (error) {
      console.error('Failed to load billing data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amountInCents: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amountInCents / 100)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleSolid className="h-5 w-5 text-green-500" />
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />
      case 'refunded':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed'
      case 'pending':
        return 'Pending'
      case 'refunded':
        return 'Refunded'
      default:
        return 'Unknown'
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-gray-200 dark:bg-gray-700 h-32 rounded-lg"></div>
          ))}
        </div>
        <div className="bg-gray-200 dark:bg-gray-700 h-96 rounded-lg"></div>
      </div>
    )
  }

  if (userType === 'professional' && showOnboarding) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Payment Setup
          </h1>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
            Set up your payment account to start receiving earnings
          </p>
        </div>
        <ProfessionalOnboarding
          professionalId={userId}
          email={userEmail}
          onOnboardingComplete={() => setShowOnboarding(false)}
        />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
          {userType === 'professional' ? 'Earnings & Payouts' : 'Billing & Payments'}
        </h1>
        <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
          {userType === 'professional'
            ? 'Track your earnings and manage payouts'
            : 'Manage your payment methods and view transaction history'
          }
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-8">
        <nav className="flex space-x-8">
          {['overview', 'transactions', 'settings'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          {userType === 'professional' && earnings ? (
            <>
              {/* Professional Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white dark:bg-[#1a1a1e] p-6 rounded-lg shadow border border-gray-100 dark:border-white/10">
                  <div className="flex items-center">
                    <BanknotesIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                    <div className="ml-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Total Earnings</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatCurrency(earnings.totalEarnings)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-[#1a1a1e] p-6 rounded-lg shadow border border-gray-100 dark:border-white/10">
                  <div className="flex items-center">
                    <ClockIcon className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                    <div className="ml-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Pending Payouts</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatCurrency(earnings.pendingPayouts)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-[#1a1a1e] p-6 rounded-lg shadow border border-gray-100 dark:border-white/10">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
                    <div className="ml-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Sessions</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {earnings.transactionCount}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-[#1a1a1e] p-6 rounded-lg shadow border border-gray-100 dark:border-white/10">
                  <div className="flex items-center">
                    <ChartBarIcon className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                    <div className="ml-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Platform Fees</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatCurrency(earnings.platformFees)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Setup Payment Account Button */}
              <div className="mb-8">
                <button
                  onClick={() => setShowOnboarding(true)}
                  className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                >
                  <BanknotesIcon className="h-5 w-5 mr-2" />
                  Manage Payment Account
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Client Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white dark:bg-[#1a1a1e] p-6 rounded-lg shadow border border-gray-100 dark:border-white/10">
                  <div className="flex items-center">
                    <CreditCardIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                    <div className="ml-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Total Spent</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatCurrency(clientPayments.reduce((sum, p) => sum + p.amount, 0))}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-[#1a1a1e] p-6 rounded-lg shadow border border-gray-100 dark:border-white/10">
                  <div className="flex items-center">
                    <CalendarDaysIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                    <div className="ml-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Sessions Booked</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {clientPayments.length}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-[#1a1a1e] p-6 rounded-lg shadow border border-gray-100 dark:border-white/10">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                    <div className="ml-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Completed</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {clientPayments.filter(p => p.status === 'completed').length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="bg-white dark:bg-[#1a1a1e] shadow rounded-lg border border-gray-100 dark:border-white/10">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-white/10">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              {userType === 'professional' ? 'Earning History' : 'Payment History'}
            </h2>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-white/10">
            {userType === 'professional' && earnings ? (
              earnings.transactions.map((transaction) => (
                <div key={transaction.id} className="px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      {getStatusIcon(transaction.status)}
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Session Payment
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatDate(transaction.created)} • Session {transaction.sessionId}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(transaction.professionalEarnings)}
                    </p>
                    <p className="text-sm text-gray-500">
                      Fee: {formatCurrency(transaction.platformFee)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              clientPayments.map((payment) => (
                <div key={payment.id} className="px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      {getStatusIcon(payment.status)}
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {payment.professionalName}
                      </p>
                      <p className="text-sm text-gray-500">
                        {payment.sessionType} • {formatDate(payment.date)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(payment.amount)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {getStatusText(payment.status)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {userType === 'professional' ? (
            <div className="bg-white dark:bg-[#1a1a1e] shadow rounded-lg p-6 border border-gray-100 dark:border-white/10">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Payout Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Payout Schedule
                  </label>
                  <select className="block w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-md shadow-sm bg-white dark:bg-[#1a1a1e] text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
                    <option>Weekly (Default)</option>
                    <option>Daily</option>
                    <option>Monthly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bank Account
                  </label>
                  <p className="text-sm text-gray-500">
                    Manage your bank account in the Stripe dashboard
                  </p>
                  <button className="mt-2 inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10">
                    <BanknotesIcon className="h-4 w-4 mr-2" />
                    Update Bank Account
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-[#1a1a1e] shadow rounded-lg p-6 border border-gray-100 dark:border-white/10">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Payment Methods</h3>
              <div className="space-y-4">
                <div className="border border-gray-200 dark:border-white/10 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CreditCardIcon className="h-6 w-6 text-gray-400 dark:text-gray-500 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">•••• •••• •••• 4242</p>
                        <p className="text-sm text-gray-500">Expires 12/25</p>
                      </div>
                    </div>
                    <button className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300">
                      Edit
                    </button>
                  </div>
                </div>
                <button className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  <CreditCardIcon className="h-4 w-4 mr-2" />
                  Add Payment Method
                </button>
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-[#1a1a1e] shadow rounded-lg p-6 border border-gray-100 dark:border-white/10">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Download Reports</h3>
            <div className="space-y-3">
              <button className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10">
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Download Transaction History
              </button>
              {userType === 'professional' && (
                <button className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10">
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                  Download Tax Documents
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}